<template>
  <div>
    <form id="purchase_form" @submit.prevent="submitInvoice">

      <div class="card">
        <div class="card-body pb-0">
          <div class="row">
            <Select2 container-class="form-group col-12 col-md-6 mt-auto mb-5" :multiple="false" :show-search="true"
              :selectClass="'form-select form-control-lg'"
              :name="`purchase-contact`"
              :label="$t('purchase.supplier_name')"
              v-model="formData.supplier_id" :IsInputGroup="true"
              icon="bi bi-plus" :clickAddon="openSupplierModal">
              <option value="">{{ $t('purchase.placeholder.contact') }}</option>
              <option v-for="contact in contacts" :value="contact.id" :key="contact.id">
                {{ contact.lastname }} {{ contact.firstname }}
              </option>
            </Select2>
            <Select2 container-class="col-12 col-md-6 mt-auto mb-5" :multiple="false" :show-search="true"
              :selectClass="'form-select form-control-lg'" name="purchase-currency" :label="$t('purchase.currency')"
              :isRequired="true" :placeholderText="$t('purchase.currency')" v-model="formData.currency_id">
              <option v-for="(currency, index) in currencies" :key="index" :value="currency.id">
                {{ currency.name + ' (' +  currency.iso + ')'}}
              </option>
            </Select2>
            <InputDate containerClass="col-12 col-md-6 mt-auto mb-5" name="purchaseDate" type="text"
              :label="$t('purchase.date')" labelClass="form-label fs-6 text-black"
              :inputClass="'form-control form-control-lg form-control-solid'" :isRequired="true" size="lg"
              :placeholder="$t('purchase.date')" v-model="formData.date" />
            <InputDate containerClass="col-12 col-md-6 mt-auto mb-5" name="purchase-delivery-date" type="text"
              :label="$t('purchase.delivery_date')" labelClass="form-label fs-6 text-black"
              :inputClass="'form-control form-control-lg form-control-solid'" size="lg"
              :placeholder="$t('purchase.delivery_date')" v-model="formData.delivery_date" />
          </div>
        </div>
      </div>

      <div class="card mt-5">
        <div class="card-body">
          <div class="pt-5">
            <div class="flex-column current">
              <div class="table-responsive">
                <table class="table table-custom table-head-custom table-vertical-center align-middle gs-4 gy-4 gx-4"
                  id="kt_advance_table_widget_2">
                  <thead>
                    <tr class="d-none d-md-flex align-items-center text-start">
                      <th class="fw-bolder col-5 required">
                        {{ $t('purchase.name') }}
                      </th>
                      <th class="fw-bolder col-2">{{ $t('purchase.lot') }}</th>
                      <th class="fw-bolder w-150px required">{{ $t('purchase.quantity') }}</th>
                      <th class="fw-bolder w-150px required">{{ $t('purchase.price') }}</th>
                      <th class="fw-bolder w-150px">{{ $t('purchase.amount') }}</th>
                      <th class="fw-bolder w-100px" style="text-align: end !important;">{{ $t('action') }}</th>
                    </tr>
                  </thead>
                  <tbody class="ms-4 me-4">
                    <PurchaseFormItem v-for="(item, index) in formData.order_contents" :key="item.product_sale_element_id" :index="index"
                      :item="item" :symbol="iso" @delete-item="removeItemRow" />
                    <div class="justify-content-end align-items-end float-end p-3">
                      <a class="btn btn-light-primary" @click="addItemRow">
                        {{ $t('purchase.add_item') }}
                      </a>
                    </div>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card mt-5">
        <div class="card-body">
          <div class="row justify-content-between">
            <div class="col-12 col-md-6">
              <div class="d-flex flex-column align-items-stretch h-100"
                v-if="formData.other_fees && formData.other_fees.length">
                <div class="flex-column current">
                  <div class="table-responsive">
                    <table class="table table-custom table-head-custom table-vertical-center align-middle">
                      <thead>
                        <tr class="">
                          <th class="fw-bolder col-6">
                            {{ $t('purchase.fee') }}
                          </th>
                          <th class="fw-bolder">{{ $t('purchase.price') }}</th>
                          <th class="fw-bolder">{{ $t('action') }}</th>
                        </tr>
                      </thead>
                      <tbody class="ms-4 me-4">
                        <PurChaseFormFee v-for="(fee, index) in formData.other_fees" :key="index" :fee="fee"
                          :index="index" @deleteFee="removeFeeRow" />
                      </tbody>
                    </table>
                  </div>
                </div>
                <div class="separator mt-auto"></div>
                <div class="d-flex justify-content-between text-warning mb-10">
                  <span class="font-weight-600">{{ $t('purchase.total_fee') }}</span>
                  <span>{{ parseFloat(totalFee).toFixed(2) }} {{ iso }}</span>
                </div>
              </div>
              <a class="btn btn-light-primary" @click="addFeeRow">
                {{ $t('purchase.add_fee') }}
              </a>
            </div>
            <div class="col-12 col-md-4">
              <div>
                <div class="border-bottom border-gray-300">
                  <div class="input-group flex-nowrap mb-3">
                    <div class="overflow-hidden flex-grow-1">
                      <Select2 container-class="form-group mt-auto" :multiple="true"
                        :selectClass="'form-control form-control-solid form-select form-select-solid rounded-end-0 border-start'"
                        :name="`purchase-taxes`" :placeholderText="$t('purchase.placeholder_taxes')"
                        :label="$t('purchase.add_taxes_to_invoice')" v-model="formData.order_taxes" :IsInputGroup="true"
                        icon="bi bi-plus" :clickAddon="openTaxModal">
                        <option v-for="(tax, index) in taxes" :key="index" :value="tax.id">{{ tax.title }}</option>
                      </Select2>
                    </div>
                  </div>
                </div>
                <div class="border-bottom border-gray-300 mt-10">
                  <div class="d-flex justify-content-between mb-3">
                    <span class="font-weight-600">{{ $t('purchase.sub_total') }}</span>
                    <span>{{ $formatLocalAmount(parseFloat(invoiceSubTotal)) }}</span>
                  </div>
                  <div class="d-flex justify-content-between mb-3">
                    <span class="font-weight-600">{{ $t('purchase.total_taxes') }}</span>
                    <span>{{ $formatLocalAmount(parseFloat(invoiceTaxesTotal)) }} {{ iso }}</span>
                  </div>
                </div>
                <div class="mt-3">
                  <div class="d-flex justify-content-between text-warning">
                    <span class="font-weight-600">{{ $t('purchase.total_ttc') }}</span>
                    <span>{{ $formatLocalAmount(parseFloat(invoiceSubTotal + invoiceTaxesTotal)) }} {{ iso }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row align-content-end justify-content-end mt-7">
            <div class="col-12 col-md-4 text-end">
              <span class="fw-bolder fs-2">{{ $t('purchase.total') }}</span>
              <br>
              <span class="fw-bolder fs-3x"> {{ $formatLocalAmount(parseFloat(invoiceTotal)) }} {{ iso }} </span>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-end mt-10">
        <div class="me-2">
          <button type="button" class="btn btn-light btn-active-light-danger">
            {{ $t('button.cancel') }}
          </button>
        </div>
        <div>
          <button type="submit" class="btn btn-primary" :disabled="processSubmitting">
            <span v-if="processSubmitting" class="spinner-border spinner-border-sm" role="status"
              aria-hidden="true"></span>
            {{ $t('button.save') }}
          </button>
        </div>
      </div>

    </form>
    <div class="modal fade" tabindex="-1" :id="'kt_modal_new_purchase_tax'">
      <div class="modal-dialog">
        <TaxForm @after-created="afterCreateTax" />
      </div>
    </div>

    <div class="modal fade" tabindex="-1" id="contactCreateModal">
      <div class="modal-dialog modal-xl">
        <CreateContact id="1" :get-countries="countries" :contact="formCreateData" :modal="true"
          @success="loadSupplier" @selectContact="selectContactId">
        </CreateContact>
      </div>
    </div>
  </div>
</template>

<script setup>

import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import Select2 from "@/components/Select2.vue";
import Input from "@/components/Input.vue";
import InputDate from "@/components/InputDate.vue";
import PurchaseFormItem from "@/components/Purchase/PurchaseFormItem.vue";
import TaxForm from "@/components/Taxe/TaxForm.vue";
import FeeForm from "@/components/Fee/FeeForm.vue";
import PurChaseFormFee from "@/components/Purchase/PurChaseFormFee.vue";
import CreateContact from "@/components/CreateContact";
import { BASE_IMAGE_URL } from "@/services/api";

const store = useStore()
const props = defineProps({
  purchase: {
    type: Object,
    default: {
      order_contents: [{}],
      order_taxes: [],
      other_fees: []
    }
  },
  submitInvoice: Function,
  processSubmitting: false
})
const emits = defineEmits(['loadingData'])

const contacts = computed(() => store.getters['contacts/getContactSuppliers'])
const currencies = computed(() => store.getters['currencies/getCurrencies'])
const taxes = computed(() => store.getters['taxes/getTaxes'])
const lots = computed(() => store.getters['products/getLots'])
const countries = computed(() => store.getters['countries/getCountries'])

const invoiceSubTotal = computed(() => {
  let total = 0
  for (const itemData of formData.value.order_contents) {
    const taxesSelected = taxes.value.filter(tax => itemData.taxes.includes(tax.id))
    let totalPrice = itemData.quantity * itemData.purchase_price
    if(itemData.lot_id){
      const lot = lots.value.find(lot => lot.id === itemData.lot_id)
      if(lot)
        totalPrice = itemData.purchase_price * itemData.quantity * lot.lot.item_number
    }
    if (taxesSelected.length > 0) {
      const totalTaxePrice = taxesSelected.reduce((accumulator, tax) => {
        if (tax.type === 'amount') {
          accumulator.accAmount += parseFloat(tax.amount);
        } else {
          accumulator.accPercent += (totalPrice * parseFloat(tax.amount) / 100);
        }
        return accumulator
      }, { accAmount: 0, accPercent: 0 })
      totalPrice += totalTaxePrice.accAmount + totalTaxePrice.accPercent
    }
    total += totalPrice
  }
  return total
})

const invoiceTaxesTotal = computed(() => {
  let total = 0
  const orderTaxesSelected = taxes.value.filter(tax => formData.value.order_taxes.includes(tax.id))
  let invoiceTota = invoiceSubTotal.value
  if (orderTaxesSelected.length > 0) {
    const totalTaxePrice = orderTaxesSelected.reduce((accumulator, tax) => {
      if (tax.type === 'amount') {
        accumulator.accAmount += parseFloat(tax.amount);
      } else {
        accumulator.accPercent += (invoiceTota * parseFloat(tax.amount) / 100);
      }
      return accumulator
    }, { accAmount: 0, accPercent: 0 })
    total = totalTaxePrice.accAmount + totalTaxePrice.accPercent
  }
  return total
})

const totalFee = computed(() => {
  let total = 0
  for (const fee of formData.value.other_fees) {
    if (isNaN(parseFloat(fee.price))) {
      total += 0
    } else {
      total += parseFloat(fee.price)
    }
  }
  return total
})

const invoiceTotal = computed(() => {
  return invoiceSubTotal.value + invoiceTaxesTotal.value + totalFee.value
})

const formData = ref(props.purchase)

const formState = ref({
  loadingData: false
})

const formCreateData = ref({
    id: null,
    gender: null,
    contactType: 'particular',
    lastname: null,
    firstname: null,
    address: {
      street: null,
      street2: null,
      city: null,
      zipcode: null,
      country: null
    },
    customer: false,
    birthday: null,
    supplier: true,
    telephone: null,
    phoneCode: null,
    previewAvatar: BASE_IMAGE_URL + '/assets/blank.png',
    email: null,
    website: null
  })

async function loadData() {
  loadSupplier()
  await store.dispatch('currencies/loadCurrencies')
  await store.dispatch('countries/loadCountries')
  await store.dispatch('products/loadAllProductSale')
  await store.dispatch('taxes/loadTaxes')
  await store.dispatch('fees/loadFees')
  await store.dispatch('products/listProductLots')
}

async function loadSupplier() {
  await store.dispatch('contacts/loadContactSupplier')
}

function addItemRow() {
  formData.value.order_contents.push({
    product_sale_element_id: "",
    comments: "",
    quantity: 1,
    purchase_price: 0,
    taxes: [],
    taxes_total_amount: 0,
    sub_total: 0,
    total: 0,
  })
}

function removeItemRow(index) {
  if (formData.value.order_contents.length) {
    formData.value.order_contents.splice(index, 1)
  }
}

function addFeeRow() {
  formData.value.other_fees.push({
    id: [],
    price: 0
  })
}

function removeFeeRow(index) {
  if (formData.value.other_fees.length) {
    formData.value.other_fees.splice(index, 1)
  }
}

function openTaxModal() {
  $("#kt_modal_new_purchase_tax").modal('toggle')
}

function afterCreateTax(tax) {
  $("#kt_modal_new_purchase_tax").modal('toggle')
  formData.value.order_taxes.push(tax?.id)
}

function openSupplierModal() {
  $('#contactCreateModal').modal('toggle')
}

function selectContactId(id){
  formData.value.supplier_id = id

  formCreateData.value = {
    id: null,
    gender: null,
    contactType: 'particular',
    lastname: null,
    firstname: null,
    address: {
      street: null,
      street2: null,
      city: null,
      zipcode: null,
      country: null
    },
    customer: false,
    birthday: null,
    supplier: true,
    telephone: null,
    phoneCode: null,
    previewAvatar: BASE_IMAGE_URL + '/assets/blank.png',
    email: null,
    website: null
  }
}

onMounted(() => {
  formState.value.loadingData = true
  emits('loadingData', false)
  Promise.all([
    loadData()
  ])
    .then(res => {
      formState.value.loadingData = false
      emits('loadingData', false)
    })
    .catch(err => {
      formState.value.loadingData = false
      emits('loadingData', false)
    })
})

</script>

<style scoped></style>